/**
 * builtin-presets.ts — 10 presets prontos para o Editor de Takes.
 *
 * Regras de câmera seguidas em todos os presets:
 *  - Full body:  Z ≥ 1.8  (evita entrar na peça)
 *  - Meio corpo: Z ≥ 0.9
 *  - Close logo: Z ≥ 0.65
 *  - FOV mínimo: 28°      (abaixo disso simula zoom óptico seguro)
 *  - FOV máximo: 55°      (acima disso distorce muito)
 */

import type { AnimationPreset } from "./take-types"

export interface BuiltinPreset {
  id: string
  preset: AnimationPreset
  label: string
  description: string
  tags: string[]
  durationSec: number
  videoUrl?: string
  thumbnailUrl?: string
  accentColor: string
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const blink = (startOffset = 1, duration = 2) => ({
  enabled: true, startOffset, duration, interval: 0.05,
})
const noBlink = { enabled: false, startOffset: 0, duration: 0, interval: 0.05 }

const lit = (intensity: number, color = "#ffffff", exposure = 1, ambient = 0.4) => ({
  intensity, color, exposure, ambientIntensity: ambient, blink: noBlink,
})

// ─── 1. CINEMATIC REVEAL (original) ──────────────────────────────────────────

const CINEMATIC_REVEAL: AnimationPreset = {
  name: "Cinematic Reveal", version: 1, fadeColor: "#000000", fadeDuration: 0.5,
  takes: [
    {
      id: "cr-1", name: "Intro Silhueta", startTime: 0, endTime: 6, easing: "easeInOut",
      camera: {
        start: { position: { x: 0, y: 1.2, z: 2.2 }, lookAt: { x: 0, y: 1.1, z: 0 } },
        end:   { position: { x: 0.4, y: 1.2, z: 2.0 }, lookAt: { x: 0, y: 1.1, z: 0 } },
        fovStart: 48, fovEnd: 46,
      },
      lighting: { ...lit(0.4, "#3b6dd6", 0.5, 0.15), blink: noBlink },
      fadeIn: false,
    },
    {
      id: "cr-2", name: "Hero Front", startTime: 6, endTime: 12, easing: "easeInOut",
      camera: {
        start: { position: { x: 0.8, y: 1.1, z: 2.2 }, lookAt: { x: 0, y: 1.2, z: 0 } },
        end:   { position: { x: 0,   y: 1.1, z: 2.2 }, lookAt: { x: 0, y: 1.2, z: 0 } },
        fovStart: 44, fovEnd: 42,
      },
      lighting: lit(1, "#ffffff", 1, 0.4),
      fadeIn: true,
    },
    {
      id: "cr-3", name: "Close Ombro", startTime: 12, endTime: 18, easing: "linear",
      camera: {
        start: { position: { x: 0.6, y: 1.55, z: 0.9 }, lookAt: { x: 0, y: 1.6, z: 0 } },
        end:   { position: { x: 0.6, y: 1.55, z: 1.1 }, lookAt: { x: 0, y: 1.6, z: 0 } },
        fovStart: 38, fovEnd: 36,
      },
      lighting: lit(1.2, "#ffffff", 1, 0.4),
      fadeIn: true,
    },
    {
      id: "cr-4", name: "Close Peito Logo", startTime: 18, endTime: 24, easing: "easeInOut",
      camera: {
        start: { position: { x: 0.2,  y: 1.48, z: 0.9 }, lookAt: { x: 0.1, y: 1.48, z: 0 } },
        end:   { position: { x: -0.2, y: 1.48, z: 0.9 }, lookAt: { x: 0.1, y: 1.48, z: 0 } },
        fovStart: 34, fovEnd: 32,
      },
      lighting: lit(1.3, "#ffffff", 1.1, 0.4),
      fadeIn: true,
    },
    {
      id: "cr-5", name: "Costas Número", startTime: 24, endTime: 30, easing: "easeInOut",
      camera: {
        start: { position: { x: -0.7, y: 1.35, z: -2.0 }, lookAt: { x: 0, y: 1.3, z: 0 } },
        end:   { position: { x:  0.7, y: 1.35, z: -2.0 }, lookAt: { x: 0, y: 1.3, z: 0 } },
        fovStart: 42, fovEnd: 42,
      },
      lighting: { ...lit(1.4, "#ffffff", 1.1, 0.4), blink: blink(2, 2) },
      fadeIn: true,
    },
    {
      id: "cr-6", name: "Hero Reset", startTime: 30, endTime: 36, easing: "easeInOut",
      camera: {
        start: { position: { x: 0, y: 1.8, z: 1.8 }, lookAt: { x: 0, y: 1.5, z: 0 } },
        end:   { position: { x: 0, y: 1.2, z: 2.4 }, lookAt: { x: 0, y: 1.1, z: 0 } },
        fovStart: 40, fovEnd: 44,
      },
      lighting: { ...lit(1.2, "#ffffff", 1, 0.5), blink: blink(1, 2) },
      fadeIn: true,
    },
    {
      id: "cr-7", name: "Outro Logo", startTime: 36, endTime: 42, easing: "linear",
      camera: {
        start: { position: { x: 0, y: 1.15, z: 3.2 }, lookAt: { x: 0, y: 1.1, z: 0 } },
        end:   { position: { x: 0, y: 1.15, z: 3.2 }, lookAt: { x: 0, y: 1.1, z: 0 } },
        fovStart: 46, fovEnd: 46,
      },
      lighting: { ...lit(0.2, "#3b6dd6", 0.6, 0.1), blink: noBlink },
      fadeIn: true,
    },
  ],
}

// ─── 2. ORBIT 360 ─────────────────────────────────────────────────────────────

const ORBIT_360: AnimationPreset = {
  name: "Orbit 360", version: 1, fadeColor: "#000000", fadeDuration: 0.4,
  takes: [
    {
      id: "o3-1", name: "Frente", startTime: 0, endTime: 8, easing: "linear",
      camera: {
        start: { position: { x: 2.0, y: 1.2, z: 0.3 }, lookAt: { x: 0, y: 1.2, z: 0 } },
        end:   { position: { x: 0.3, y: 1.2, z: 2.0 }, lookAt: { x: 0, y: 1.2, z: 0 } },
        fovStart: 44, fovEnd: 44,
      },
      lighting: lit(1, "#ffffff", 1, 0.4),
      fadeIn: false,
    },
    {
      id: "o3-2", name: "Lateral → Costas", startTime: 8, endTime: 16, easing: "linear",
      camera: {
        start: { position: { x: 0.3,  y: 1.2, z: 2.0 }, lookAt: { x: 0, y: 1.2, z: 0 } },
        end:   { position: { x: -2.0, y: 1.2, z: 0.3 }, lookAt: { x: 0, y: 1.2, z: 0 } },
        fovStart: 44, fovEnd: 44,
      },
      lighting: lit(1.1, "#ffffff", 1, 0.4),
      fadeIn: false,
    },
    {
      id: "o3-3", name: "Costas → Frente", startTime: 16, endTime: 24, easing: "linear",
      camera: {
        start: { position: { x: -2.0, y: 1.2, z:  0.3 }, lookAt: { x: 0, y: 1.2, z: 0 } },
        end:   { position: { x: -0.3, y: 1.2, z: -2.0 }, lookAt: { x: 0, y: 1.2, z: 0 } },
        fovStart: 44, fovEnd: 44,
      },
      lighting: lit(1.1, "#ffffff", 1, 0.4),
      fadeIn: false,
    },
    {
      id: "o3-4", name: "Fechar Frente", startTime: 24, endTime: 30, easing: "easeInOut",
      camera: {
        start: { position: { x: -0.1, y: 1.2, z: -2.0 }, lookAt: { x: 0, y: 1.2, z: 0 } },
        end:   { position: { x: 0,    y: 1.2, z:  2.2 }, lookAt: { x: 0, y: 1.2, z: 0 } },
        fovStart: 44, fovEnd: 42,
      },
      lighting: { ...lit(1.2, "#ffffff", 1, 0.4), blink: blink(1, 2) },
      fadeIn: true,
    },
  ],
}

// ─── 3. EDITORIAL ─────────────────────────────────────────────────────────────

const EDITORIAL: AnimationPreset = {
  name: "Editorial", version: 1, fadeColor: "#000000", fadeDuration: 0.8,
  takes: [
    {
      id: "ed-1", name: "Silhueta Lenta", startTime: 0, endTime: 8, easing: "linear",
      camera: {
        start: { position: { x: -0.4, y: 1.4, z: 2.8 }, lookAt: { x: 0, y: 1.2, z: 0 } },
        end:   { position: { x:  0.4, y: 1.4, z: 2.8 }, lookAt: { x: 0, y: 1.2, z: 0 } },
        fovStart: 46, fovEnd: 46,
      },
      lighting: { ...lit(0.2, "#4060c0", 0.4, 0.05), blink: noBlink },
      fadeIn: false,
    },
    {
      id: "ed-2", name: "Reveal Frontal", startTime: 8, endTime: 18, easing: "easeOutCubic",
      camera: {
        start: { position: { x: 0, y: 0.9, z: 3.0 }, lookAt: { x: 0, y: 1.0, z: 0 } },
        end:   { position: { x: 0, y: 1.3, z: 2.2 }, lookAt: { x: 0, y: 1.2, z: 0 } },
        fovStart: 50, fovEnd: 42,
      },
      lighting: lit(1, "#fff5e0", 1.1, 0.3),
      fadeIn: true, fadeDuration: 1.2,
    },
    {
      id: "ed-3", name: "Lateral Devagar", startTime: 18, endTime: 30, easing: "linear",
      camera: {
        start: { position: { x:  1.8, y: 1.3, z: 0.8 }, lookAt: { x: 0, y: 1.3, z: 0 } },
        end:   { position: { x: -1.8, y: 1.3, z: 0.8 }, lookAt: { x: 0, y: 1.3, z: 0 } },
        fovStart: 42, fovEnd: 42,
      },
      lighting: lit(1.1, "#fff5e0", 1.1, 0.35),
      fadeIn: true, fadeDuration: 0.8,
    },
    {
      id: "ed-4", name: "Meio Corpo", startTime: 30, endTime: 38, easing: "easeInOut",
      camera: {
        start: { position: { x:  0.3, y: 1.5, z: 1.2 }, lookAt: { x: 0.1, y: 1.5, z: 0 } },
        end:   { position: { x: -0.3, y: 1.5, z: 1.2 }, lookAt: { x: 0,   y: 1.5, z: 0 } },
        fovStart: 38, fovEnd: 36,
      },
      lighting: lit(1.3, "#fff8ee", 1.2, 0.25),
      fadeIn: true, fadeDuration: 0.6,
    },
    {
      id: "ed-5", name: "Fade Out", startTime: 38, endTime: 46, easing: "easeInOutCubic",
      camera: {
        start: { position: { x: 0, y: 1.2, z: 2.2 }, lookAt: { x: 0, y: 1.1, z: 0 } },
        end:   { position: { x: 0, y: 1.2, z: 2.8 }, lookAt: { x: 0, y: 1.1, z: 0 } },
        fovStart: 42, fovEnd: 46,
      },
      lighting: { ...lit(0.3, "#4060c0", 0.5, 0.1), blink: noBlink },
      fadeIn: true, fadeDuration: 1.5,
    },
  ],
}

// ─── 4. QUICK SHOWCASE ────────────────────────────────────────────────────────

const QUICK_SHOWCASE: AnimationPreset = {
  name: "Quick Showcase", version: 1, fadeColor: "#000000", fadeDuration: 0.3,
  takes: [
    {
      id: "qs-1", name: "Abertura", startTime: 0, endTime: 5, easing: "easeOutCubic",
      camera: {
        start: { position: { x: 0, y: 1.8, z: 2.8 }, lookAt: { x: 0, y: 1.3, z: 0 } },
        end:   { position: { x: 0, y: 1.3, z: 2.2 }, lookAt: { x: 0, y: 1.2, z: 0 } },
        fovStart: 52, fovEnd: 44,
      },
      lighting: lit(0.7, "#c0d8ff", 0.7, 0.2),
      fadeIn: false,
    },
    {
      id: "qs-2", name: "Arco Frente", startTime: 5, endTime: 12, easing: "linear",
      camera: {
        start: { position: { x:  1.8, y: 1.2, z: 0.8 }, lookAt: { x: 0, y: 1.2, z: 0 } },
        end:   { position: { x: -1.8, y: 1.2, z: 0.8 }, lookAt: { x: 0, y: 1.2, z: 0 } },
        fovStart: 44, fovEnd: 44,
      },
      lighting: lit(1.2, "#ffffff", 1, 0.4),
      fadeIn: true,
    },
    {
      id: "qs-3", name: "Meio Corpo", startTime: 12, endTime: 18, easing: "easeInOut",
      camera: {
        start: { position: { x: -0.3, y: 1.5, z: 1.2 }, lookAt: { x: 0, y: 1.5, z: 0 } },
        end:   { position: { x:  0.3, y: 1.5, z: 1.2 }, lookAt: { x: 0, y: 1.5, z: 0 } },
        fovStart: 38, fovEnd: 36,
      },
      lighting: lit(1.4, "#ffffff", 1.2, 0.3),
      fadeIn: true,
    },
    {
      id: "qs-4", name: "Costas", startTime: 18, endTime: 24, easing: "easeInOut",
      camera: {
        start: { position: { x:  0.5, y: 1.3, z: -2.2 }, lookAt: { x: 0, y: 1.3, z: 0 } },
        end:   { position: { x: -0.5, y: 1.3, z: -2.2 }, lookAt: { x: 0, y: 1.3, z: 0 } },
        fovStart: 42, fovEnd: 42,
      },
      lighting: { ...lit(1.3, "#ffffff", 1, 0.4), blink: blink(1, 1.5) },
      fadeIn: true,
    },
    {
      id: "qs-5", name: "Pull Back", startTime: 24, endTime: 30, easing: "easeInOutCubic",
      camera: {
        start: { position: { x: 0, y: 1.3, z: 2.2 }, lookAt: { x: 0, y: 1.2, z: 0 } },
        end:   { position: { x: 0, y: 1.6, z: 3.5 }, lookAt: { x: 0, y: 1.1, z: 0 } },
        fovStart: 44, fovEnd: 50,
      },
      lighting: { ...lit(0.8, "#d0e8ff", 0.8, 0.3), blink: noBlink },
      fadeIn: true,
    },
  ],
}

// ─── 5. HYPE ──────────────────────────────────────────────────────────────────

const HYPE: AnimationPreset = {
  name: "Hype", version: 1, fadeColor: "#000000", fadeDuration: 0.15,
  takes: [
    {
      id: "hy-1", name: "Entrada Rápida", startTime: 0, endTime: 3, easing: "easeOutBack",
      camera: {
        start: { position: { x: 0, y: 1.3, z: 4.0 }, lookAt: { x: 0, y: 1.2, z: 0 } },
        end:   { position: { x: 0, y: 1.3, z: 2.2 }, lookAt: { x: 0, y: 1.2, z: 0 } },
        fovStart: 54, fovEnd: 44,
      },
      lighting: lit(1.6, "#ffffff", 1.3, 0.5),
      fadeIn: false,
    },
    {
      id: "hy-2", name: "Spin", startTime: 3, endTime: 7, easing: "easeInOut",
      camera: {
        start: { position: { x:  1.8, y: 1.2, z: 1.0 }, lookAt: { x: 0, y: 1.2, z: 0 } },
        end:   { position: { x: -1.8, y: 1.2, z: 1.0 }, lookAt: { x: 0, y: 1.2, z: 0 } },
        fovStart: 44, fovEnd: 44,
      },
      lighting: lit(1.6, "#ffffff", 1.2, 0.5),
      fadeIn: true,
    },
    {
      id: "hy-3", name: "Logo Flash", startTime: 7, endTime: 10, easing: "easeOutCubic",
      camera: {
        start: { position: { x: 0.3, y: 1.5, z: 1.1 }, lookAt: { x: 0, y: 1.5, z: 0 } },
        end:   { position: { x: 0,   y: 1.5, z: 0.8 }, lookAt: { x: 0, y: 1.5, z: 0 } },
        fovStart: 40, fovEnd: 32,
      },
      lighting: { ...lit(2, "#ffffff", 1.5, 0.4), blink: blink(0, 2) },
      fadeIn: true,
    },
    {
      id: "hy-4", name: "Costas", startTime: 10, endTime: 14, easing: "easeOutCubic",
      camera: {
        start: { position: { x:  0.8, y: 1.3, z: -2.2 }, lookAt: { x: 0, y: 1.3, z: 0 } },
        end:   { position: { x: -0.8, y: 1.3, z: -2.2 }, lookAt: { x: 0, y: 1.3, z: 0 } },
        fovStart: 46, fovEnd: 44,
      },
      lighting: { ...lit(1.8, "#ffffff", 1.4, 0.5), blink: blink(0, 3) },
      fadeIn: true,
    },
    {
      id: "hy-5", name: "Hero Final", startTime: 14, endTime: 18, easing: "easeOutBack",
      camera: {
        start: { position: { x: 0, y: 1.8, z: 2.0 }, lookAt: { x: 0, y: 1.3, z: 0 } },
        end:   { position: { x: 0, y: 1.2, z: 2.4 }, lookAt: { x: 0, y: 1.1, z: 0 } },
        fovStart: 50, fovEnd: 44,
      },
      lighting: lit(1.4, "#ffffff", 1.2, 0.4),
      fadeIn: true,
    },
  ],
}

// ─── 6. DIAGONAL FLOW ────────────────────────────────────────────────────────

const DIAGONAL_FLOW: AnimationPreset = {
  name: "Diagonal Flow", version: 1, fadeColor: "#000000", fadeDuration: 0.5,
  takes: [
    {
      id: "df-1", name: "Diagonal Baixo", startTime: 0, endTime: 7, easing: "easeInOut",
      camera: {
        start: { position: { x: -1.0, y: 0.7, z: 2.5 }, lookAt: { x: 0, y: 0.8, z: 0 } },
        end:   { position: { x:  0.5, y: 1.5, z: 2.2 }, lookAt: { x: 0, y: 1.3, z: 0 } },
        fovStart: 50, fovEnd: 44,
      },
      lighting: lit(0.8, "#e8f0ff", 0.9, 0.25),
      fadeIn: false,
    },
    {
      id: "df-2", name: "Ombro Direito", startTime: 7, endTime: 14, easing: "linear",
      camera: {
        start: { position: { x: 0.8, y: 1.6, z: 1.2 }, lookAt: { x: 0.2, y: 1.6, z: 0 } },
        end:   { position: { x: 1.0, y: 1.3, z: 1.5 }, lookAt: { x: 0.1, y: 1.3, z: 0 } },
        fovStart: 38, fovEnd: 40,
      },
      lighting: lit(1.2, "#ffffff", 1.1, 0.35),
      fadeIn: true,
    },
    {
      id: "df-3", name: "Frente Centro", startTime: 14, endTime: 21, easing: "easeInOut",
      camera: {
        start: { position: { x: 0.5, y: 1.2, z: 2.2 }, lookAt: { x: 0, y: 1.2, z: 0 } },
        end:   { position: { x: -0.5, y: 1.2, z: 2.2 }, lookAt: { x: 0, y: 1.2, z: 0 } },
        fovStart: 44, fovEnd: 44,
      },
      lighting: lit(1.2, "#ffffff", 1, 0.4),
      fadeIn: true,
    },
    {
      id: "df-4", name: "Diagonal Cima", startTime: 21, endTime: 28, easing: "easeInOut",
      camera: {
        start: { position: { x: -0.5, y: 1.2, z: 2.2 }, lookAt: { x: 0, y: 1.2, z: 0 } },
        end:   { position: { x:  0.8, y: 2.0, z: 2.5 }, lookAt: { x: 0, y: 1.4, z: 0 } },
        fovStart: 44, fovEnd: 48,
      },
      lighting: { ...lit(1, "#ffffff", 1, 0.4), blink: blink(1, 2) },
      fadeIn: true,
    },
  ],
}

// ─── 7. STUDIO CLEAN ─────────────────────────────────────────────────────────

const STUDIO_CLEAN: AnimationPreset = {
  name: "Studio Clean", version: 1, fadeColor: "#ffffff", fadeDuration: 0.6,
  takes: [
    {
      id: "sc-1", name: "Full Body Estático", startTime: 0, endTime: 8, easing: "linear",
      camera: {
        start: { position: { x: -0.2, y: 1.2, z: 2.5 }, lookAt: { x: 0, y: 1.1, z: 0 } },
        end:   { position: { x:  0.2, y: 1.2, z: 2.5 }, lookAt: { x: 0, y: 1.1, z: 0 } },
        fovStart: 44, fovEnd: 44,
      },
      lighting: lit(1.2, "#ffffff", 1.1, 0.6),
      fadeIn: false,
    },
    {
      id: "sc-2", name: "Meio Corpo Frente", startTime: 8, endTime: 16, easing: "linear",
      camera: {
        start: { position: { x: -0.2, y: 1.5, z: 1.4 }, lookAt: { x: 0, y: 1.5, z: 0 } },
        end:   { position: { x:  0.2, y: 1.5, z: 1.4 }, lookAt: { x: 0, y: 1.5, z: 0 } },
        fovStart: 38, fovEnd: 38,
      },
      lighting: lit(1.3, "#ffffff", 1.1, 0.55),
      fadeIn: true,
    },
    {
      id: "sc-3", name: "Full Body Costas", startTime: 16, endTime: 24, easing: "linear",
      camera: {
        start: { position: { x:  0.2, y: 1.2, z: -2.5 }, lookAt: { x: 0, y: 1.1, z: 0 } },
        end:   { position: { x: -0.2, y: 1.2, z: -2.5 }, lookAt: { x: 0, y: 1.1, z: 0 } },
        fovStart: 44, fovEnd: 44,
      },
      lighting: lit(1.2, "#ffffff", 1.1, 0.6),
      fadeIn: true,
    },
    {
      id: "sc-4", name: "Meio Corpo Costas", startTime: 24, endTime: 32, easing: "linear",
      camera: {
        start: { position: { x:  0.2, y: 1.5, z: -1.4 }, lookAt: { x: 0, y: 1.5, z: 0 } },
        end:   { position: { x: -0.2, y: 1.5, z: -1.4 }, lookAt: { x: 0, y: 1.5, z: 0 } },
        fovStart: 38, fovEnd: 38,
      },
      lighting: lit(1.3, "#ffffff", 1.1, 0.55),
      fadeIn: true,
    },
  ],
}

// ─── 8. ATMOSPHERE ───────────────────────────────────────────────────────────

const ATMOSPHERE: AnimationPreset = {
  name: "Atmosphere", version: 1, fadeColor: "#000000", fadeDuration: 1.0,
  takes: [
    {
      id: "at-1", name: "Névoa Azul", startTime: 0, endTime: 8, easing: "linear",
      camera: {
        start: { position: { x: -0.5, y: 1.3, z: 3.0 }, lookAt: { x: 0, y: 1.2, z: 0 } },
        end:   { position: { x:  0.5, y: 1.3, z: 3.0 }, lookAt: { x: 0, y: 1.2, z: 0 } },
        fovStart: 46, fovEnd: 46,
      },
      lighting: { ...lit(0.3, "#2040a0", 0.4, 0.08), blink: noBlink },
      fadeIn: false,
    },
    {
      id: "at-2", name: "Iluminação Dramática", startTime: 8, endTime: 18, easing: "easeOutCubic",
      camera: {
        start: { position: { x: 0.6, y: 0.9, z: 3.0 }, lookAt: { x: 0, y: 1.0, z: 0 } },
        end:   { position: { x: 0,   y: 1.2, z: 2.4 }, lookAt: { x: 0, y: 1.2, z: 0 } },
        fovStart: 50, fovEnd: 44,
      },
      lighting: lit(1.2, "#ffe8c0", 1.2, 0.2),
      fadeIn: true, fadeDuration: 1.5,
    },
    {
      id: "at-3", name: "Lateral Quente", startTime: 18, endTime: 28, easing: "linear",
      camera: {
        start: { position: { x: 1.6, y: 1.3, z: 1.0 }, lookAt: { x: 0, y: 1.3, z: 0 } },
        end:   { position: { x: 1.6, y: 1.3, z: -1.0 }, lookAt: { x: 0, y: 1.3, z: 0 } },
        fovStart: 44, fovEnd: 44,
      },
      lighting: lit(1.3, "#ffcc88", 1.2, 0.25),
      fadeIn: true,
    },
    {
      id: "at-4", name: "Fade Azul", startTime: 28, endTime: 36, easing: "easeInOutCubic",
      camera: {
        start: { position: { x: 0, y: 1.2, z: 2.4 }, lookAt: { x: 0, y: 1.2, z: 0 } },
        end:   { position: { x: 0, y: 1.4, z: 3.0 }, lookAt: { x: 0, y: 1.1, z: 0 } },
        fovStart: 44, fovEnd: 48,
      },
      lighting: { ...lit(0.2, "#2040a0", 0.4, 0.06), blink: noBlink },
      fadeIn: true, fadeDuration: 1.8,
    },
  ],
}

// ─── 9. TRÊS ÂNGULOS ─────────────────────────────────────────────────────────

const TRES_ANGULOS: AnimationPreset = {
  name: "Três Ângulos", version: 1, fadeColor: "#000000", fadeDuration: 0.4,
  takes: [
    {
      id: "ta-1", name: "Frente", startTime: 0, endTime: 8, easing: "easeInOut",
      camera: {
        start: { position: { x:  0.5, y: 1.2, z: 2.3 }, lookAt: { x: 0, y: 1.2, z: 0 } },
        end:   { position: { x: -0.5, y: 1.2, z: 2.3 }, lookAt: { x: 0, y: 1.2, z: 0 } },
        fovStart: 44, fovEnd: 44,
      },
      lighting: lit(1.1, "#ffffff", 1, 0.4),
      fadeIn: false,
    },
    {
      id: "ta-2", name: "Lateral", startTime: 8, endTime: 16, easing: "easeInOut",
      camera: {
        start: { position: { x: 2.0, y: 1.2, z: -0.5 }, lookAt: { x: 0, y: 1.2, z: 0 } },
        end:   { position: { x: 2.0, y: 1.2, z:  0.5 }, lookAt: { x: 0, y: 1.2, z: 0 } },
        fovStart: 44, fovEnd: 44,
      },
      lighting: lit(1.1, "#ffffff", 1, 0.4),
      fadeIn: true,
    },
    {
      id: "ta-3", name: "Costas", startTime: 16, endTime: 24, easing: "easeInOut",
      camera: {
        start: { position: { x: -0.5, y: 1.2, z: -2.3 }, lookAt: { x: 0, y: 1.2, z: 0 } },
        end:   { position: { x:  0.5, y: 1.2, z: -2.3 }, lookAt: { x: 0, y: 1.2, z: 0 } },
        fovStart: 44, fovEnd: 44,
      },
      lighting: lit(1.1, "#ffffff", 1, 0.4),
      fadeIn: true,
    },
    {
      id: "ta-4", name: "Detalhe Costas", startTime: 24, endTime: 30, easing: "easeInOut",
      camera: {
        start: { position: { x:  0.3, y: 1.5, z: -1.0 }, lookAt: { x: 0, y: 1.5, z: 0 } },
        end:   { position: { x: -0.3, y: 1.5, z: -1.0 }, lookAt: { x: 0, y: 1.5, z: 0 } },
        fovStart: 38, fovEnd: 36,
      },
      lighting: { ...lit(1.3, "#ffffff", 1.1, 0.35), blink: blink(1, 2) },
      fadeIn: true,
    },
  ],
}

// ─── 10. GOLDEN HOUR ─────────────────────────────────────────────────────────

const GOLDEN_HOUR: AnimationPreset = {
  name: "Golden Hour", version: 1, fadeColor: "#1a0800", fadeDuration: 0.7,
  takes: [
    {
      id: "gh-1", name: "Contraluz", startTime: 0, endTime: 7, easing: "easeInOut",
      camera: {
        start: { position: { x: -0.6, y: 1.1, z: 2.6 }, lookAt: { x: 0, y: 1.1, z: 0 } },
        end:   { position: { x:  0.6, y: 1.1, z: 2.6 }, lookAt: { x: 0, y: 1.1, z: 0 } },
        fovStart: 48, fovEnd: 46,
      },
      lighting: { ...lit(0.5, "#ff6020", 0.6, 0.1), blink: noBlink },
      fadeIn: false,
    },
    {
      id: "gh-2", name: "Ilumina Frontal", startTime: 7, endTime: 16, easing: "easeOutCubic",
      camera: {
        start: { position: { x: 0.4, y: 0.9, z: 2.8 }, lookAt: { x: 0, y: 1.0, z: 0 } },
        end:   { position: { x: 0,   y: 1.3, z: 2.3 }, lookAt: { x: 0, y: 1.2, z: 0 } },
        fovStart: 50, fovEnd: 44,
      },
      lighting: lit(1.3, "#ffd080", 1.2, 0.3),
      fadeIn: true, fadeDuration: 1.0,
    },
    {
      id: "gh-3", name: "Lateral Dourada", startTime: 16, endTime: 24, easing: "linear",
      camera: {
        start: { position: { x: 1.6, y: 1.3, z: 0.8 }, lookAt: { x: 0, y: 1.3, z: 0 } },
        end:   { position: { x: 1.6, y: 1.3, z: -0.8 }, lookAt: { x: 0, y: 1.3, z: 0 } },
        fovStart: 44, fovEnd: 44,
      },
      lighting: lit(1.4, "#ffcc60", 1.3, 0.25),
      fadeIn: true,
    },
    {
      id: "gh-4", name: "Meio Corpo", startTime: 24, endTime: 30, easing: "easeInOut",
      camera: {
        start: { position: { x: -0.3, y: 1.5, z: 1.3 }, lookAt: { x: 0, y: 1.5, z: 0 } },
        end:   { position: { x:  0.3, y: 1.5, z: 1.3 }, lookAt: { x: 0, y: 1.5, z: 0 } },
        fovStart: 38, fovEnd: 36,
      },
      lighting: { ...lit(1.5, "#ffd080", 1.3, 0.3), blink: blink(1, 2) },
      fadeIn: true,
    },
    {
      id: "gh-5", name: "Pôr do Sol", startTime: 30, endTime: 38, easing: "easeInOutCubic",
      camera: {
        start: { position: { x: 0, y: 1.2, z: 2.3 }, lookAt: { x: 0, y: 1.1, z: 0 } },
        end:   { position: { x: 0, y: 1.4, z: 3.0 }, lookAt: { x: 0, y: 1.0, z: 0 } },
        fovStart: 44, fovEnd: 50,
      },
      lighting: { ...lit(0.4, "#ff5010", 0.5, 0.08), blink: noBlink },
      fadeIn: true, fadeDuration: 1.5,
    },
  ],
}

// ─── 11. CINEMATIC SHIRT (briefing detalhado) ─────────────────────────────────
//
// Preset baseado em briefing cinematográfico preciso.
// lookAt fixo: centro peito/gola da camisa { x:0, y:0.55, z:0 }
// FOV: 35° em todos os takes
// Micro-oscilações < 2cm em x/y para aparência orgânica
// Todos os shots usam easeInOut conforme especificação

const CINEMATIC_SHIRT: AnimationPreset = {
  name: "Cinematic Shirt", version: 1, fadeColor: "#000000", fadeDuration: 0.4,
  takes: [
    // ── SHOT 01 — REVEAL ─────────────────────────────────────────────────────
    // Dolly in lento + pequena subida vertical. Sem orbit.
    {
      id: "cs-1", name: "Shot 01 – Reveal",
      startTime: 0, endTime: 2, easing: "easeInOut",
      camera: {
        start: { position: { x: 0.01, y: 0.25, z: 4.5  }, lookAt: { x: 0, y: 0.55, z: 0 } },
        end:   { position: { x: 0.01, y: 0.40, z: 3.8  }, lookAt: { x: 0, y: 0.55, z: 0 } },
        fovStart: 35, fovEnd: 35,
      },
      lighting: { intensity: 0.7, color: "#d8e8ff", exposure: 0.85, ambientIntensity: 0.2, blink: { enabled: false, startOffset: 0, duration: 0, interval: 0.05 } },
      fadeIn: false,
    },

    // ── SHOT 02 — FRONTAL PARA 3/4 ───────────────────────────────────────────
    // Orbit 0°→45°, raio 3.8, leve aproximação.
    // start: orbit(0°, 3.8) = (0, 0.40, 3.8)
    // end:   orbit(45°, 3.8) = (2.687, 0.45, 2.687)
    // Micro-oscilação: +0.01 em x no start para naturalidade
    {
      id: "cs-2", name: "Shot 02 – Frontal → 3/4",
      startTime: 2, endTime: 4, easing: "easeInOut",
      camera: {
        start: { position: { x: 0.01,  y: 0.40, z: 3.80 }, lookAt: { x: 0, y: 0.55, z: 0 } },
        end:   { position: { x: 2.69,  y: 0.45, z: 2.70 }, lookAt: { x: 0, y: 0.55, z: 0 } },
        fovStart: 35, fovEnd: 35,
      },
      lighting: { intensity: 0.9, color: "#ffffff", exposure: 0.95, ambientIntensity: 0.3, blink: { enabled: false, startOffset: 0, duration: 0, interval: 0.05 } },
      fadeIn: false,
    },

    // ── SHOT 03 — 3/4 PARA LATERAL ───────────────────────────────────────────
    // Orbit 45°→90°, raio 3.6, velocidade constante.
    // start: orbit(45°, 3.6) = (2.546, 0.45, 2.546)
    // end:   orbit(90°, 3.6) = (3.6,   0.45, 0.0)
    // Micro-oscilação: raio levemente variado (+0.02) para quebrar círculo perfeito
    {
      id: "cs-3", name: "Shot 03 – 3/4 → Lateral",
      startTime: 4, endTime: 6, easing: "easeInOut",
      camera: {
        start: { position: { x: 2.55,  y: 0.45, z: 2.56 }, lookAt: { x: 0, y: 0.55, z: 0 } },
        end:   { position: { x: 3.62,  y: 0.45, z: 0.02 }, lookAt: { x: 0, y: 0.55, z: 0 } },
        fovStart: 35, fovEnd: 35,
      },
      lighting: { intensity: 1.0, color: "#ffffff", exposure: 1.0, ambientIntensity: 0.35, blink: { enabled: false, startOffset: 0, duration: 0, interval: 0.05 } },
      fadeIn: false,
    },

    // ── SHOT 04 — LATERAL PARA COSTAS ────────────────────────────────────────
    // Orbit 90°→180°, raio 3.6, leve desaceleração ao chegar nas costas
    // (easeInOut já provê isso naturalmente).
    // start: (3.6, 0.45, 0.0)
    // end:   (0.0, 0.45, -3.6)
    // Micro-oscilação: -0.01 em z no start
    {
      id: "cs-4", name: "Shot 04 – Lateral → Costas",
      startTime: 6, endTime: 8, easing: "easeInOut",
      camera: {
        start: { position: { x: 3.60,  y: 0.45, z: -0.01 }, lookAt: { x: 0, y: 0.55, z: 0 } },
        end:   { position: { x: 0.02,  y: 0.45, z: -3.62 }, lookAt: { x: 0, y: 0.55, z: 0 } },
        fovStart: 35, fovEnd: 35,
      },
      lighting: { intensity: 1.1, color: "#ffffff", exposure: 1.0, ambientIntensity: 0.4, blink: { enabled: false, startOffset: 0, duration: 0, interval: 0.05 } },
      fadeIn: false,
    },

    // ── SHOT 05 — CLOSE DETALHE 01 ───────────────────────────────────────────
    // Slider vertical para cima + dolly in pequeno.
    // start: (0.15, 0.40, 0.80) → end: (0.15, 0.65, 0.55)
    {
      id: "cs-5", name: "Shot 05 – Close Detalhe 01",
      startTime: 8, endTime: 10, easing: "easeInOut",
      camera: {
        start: { position: { x: 0.15, y: 0.40, z: 0.80 }, lookAt: { x: 0, y: 0.55, z: 0 } },
        end:   { position: { x: 0.15, y: 0.65, z: 0.55 }, lookAt: { x: 0, y: 0.55, z: 0 } },
        fovStart: 35, fovEnd: 35,
      },
      lighting: { intensity: 1.3, color: "#ffffff", exposure: 1.1, ambientIntensity: 0.3, blink: { enabled: false, startOffset: 0, duration: 0, interval: 0.05 } },
      fadeIn: true, fadeDuration: 0.4,
    },

    // ── SHOT 06 — CLOSE DETALHE 02 ───────────────────────────────────────────
    // Travelling horizontal, distância constante.
    // start: (-0.20, 0.75, 0.60) → end: (0.20, 0.75, 0.60)
    {
      id: "cs-6", name: "Shot 06 – Close Detalhe 02",
      startTime: 10, endTime: 12, easing: "easeInOut",
      camera: {
        start: { position: { x: -0.20, y: 0.75, z: 0.60 }, lookAt: { x: 0, y: 0.55, z: 0 } },
        end:   { position: { x:  0.20, y: 0.75, z: 0.60 }, lookAt: { x: 0, y: 0.55, z: 0 } },
        fovStart: 35, fovEnd: 35,
      },
      lighting: { intensity: 1.3, color: "#ffffff", exposure: 1.1, ambientIntensity: 0.3, blink: { enabled: false, startOffset: 0, duration: 0, interval: 0.05 } },
      fadeIn: false,
    },

    // ── SHOT 07 — RETORNO AO PLANO GERAL ─────────────────────────────────────
    // Dolly out rápido porém suave + correção de altura.
    // start: (0.20, 0.75, 0.60) → end: (-0.50, 0.45, 4.00)
    {
      id: "cs-7", name: "Shot 07 – Retorno Plano Geral",
      startTime: 12, endTime: 13.5, easing: "easeInOut",
      camera: {
        start: { position: { x:  0.20, y: 0.75, z: 0.60 }, lookAt: { x: 0, y: 0.55, z: 0 } },
        end:   { position: { x: -0.50, y: 0.45, z: 4.00 }, lookAt: { x: 0, y: 0.55, z: 0 } },
        fovStart: 35, fovEnd: 35,
      },
      lighting: { intensity: 1.0, color: "#ffffff", exposure: 1.0, ambientIntensity: 0.35, blink: { enabled: false, startOffset: 0, duration: 0, interval: 0.05 } },
      fadeIn: false,
    },

    // ── SHOT 08 — ORBIT FINAL ────────────────────────────────────────────────
    // Orbit 180°→270°, raio 4.0, subida 0.45→0.55.
    // start: orbit(180°, 4.0) = (0.0,  0.45, -4.0)
    // end:   orbit(270°, 4.0) = (-4.0, 0.55, 0.0)  ← 3/4 esquerda
    // Micro-oscilação: raio start +0.02 e end +0.01 para quebrar círculo
    {
      id: "cs-8", name: "Shot 08 – Orbit Final",
      startTime: 13.5, endTime: 18, easing: "easeInOut",
      camera: {
        start: { position: { x:  0.02, y: 0.45, z: -4.02 }, lookAt: { x: 0, y: 0.55, z: 0 } },
        end:   { position: { x: -4.01, y: 0.55, z: -0.01 }, lookAt: { x: 0, y: 0.55, z: 0 } },
        fovStart: 35, fovEnd: 35,
      },
      lighting: { intensity: 1.0, color: "#e8f0ff", exposure: 0.95, ambientIntensity: 0.35, blink: { enabled: false, startOffset: 0, duration: 0, interval: 0.05 } },
      fadeIn: false,
    },
  ],
}

// ─── 12. MULTI-CLONE REVEAL ──────────────────────────────────────────────────
// Análise precisa de 241 frames (10s @ 24.1fps):
//
// Clones surgem do centro (0–1s), giram em velocidades individuais (1–6.8s),
// somem sequencialmente (6.8–8.5s), câmera orbita no solo (8.5–10s).
//
// CÂMERA estática em 3/4 direita durante toda a fase de clones (0→8.5s).
// Velocidades medidas frame a frame:
//   central=1.682 rad/s, médios=1.377 rad/s, extremos=1.165 rad/s

const MULTI_CLONE_REVEAL: AnimationPreset = {
  name: "Multi-Clone Reveal", version: 1, fadeColor: "#000000", fadeDuration: 0,
  // @ts-ignore
  useCloneSequence: true,
  takes: [
    { id: "mc-1", name: "Take 1 – Leque", startTime: 0, endTime: 3, easing: "linear",
      camera: { start: { position: { x: 0.3, y: 1.2, z: 4.5 }, lookAt: { x: 0, y: 1.1, z: 0 } },
                end:   { position: { x: 0.3, y: 1.2, z: 4.5 }, lookAt: { x: 0, y: 1.1, z: 0 } },
                fovStart: 52, fovEnd: 52 },
      lighting: lit(1.0, "#ffffff", 1, 0.45), fadeIn: false },
    { id: "mc-2", name: "Take 2 – Carrossel", startTime: 3, endTime: 5, easing: "linear",
      camera: { start: { position: { x: 0.3, y: 1.2, z: 4.5 }, lookAt: { x: 0, y: 1.1, z: 0 } },
                end:   { position: { x: 0.3, y: 1.2, z: 4.5 }, lookAt: { x: 0, y: 1.1, z: 0 } },
                fovStart: 52, fovEnd: 52 },
      lighting: lit(1.0, "#ffffff", 1, 0.45), fadeIn: false },
    { id: "mc-3", name: "Take 3 – Orbit Lateral", startTime: 5, endTime: 7.3, easing: "easeInOut",
      camera: { start: { position: { x: 0.3, y: 1.2, z: 4.5 }, lookAt: { x: 0, y: 1.1, z: 0 } },
                end:   { position: { x: 3.9, y: 1.2, z: 3.9 }, lookAt: { x: 0, y: 1.1, z: 0 } },
                fovStart: 52, fovEnd: 56 },
      lighting: lit(1.0, "#ffffff", 1, 0.45), fadeIn: false },
    { id: "mc-4", name: "Take 4 – Linear 360°", startTime: 7.3, endTime: 8.5, easing: "easeInOut",
      camera: { start: { position: { x: 3.9, y: 1.2, z: 3.9 }, lookAt: { x: 0, y: 1.1, z: 0 } },
                end:   { position: { x: 0.3, y: 1.2, z: 4.5 }, lookAt: { x: 0, y: 1.1, z: 0 } },
                fovStart: 56, fovEnd: 52 },
      lighting: lit(1.0, "#ffffff", 1, 0.45), fadeIn: false },
    { id: "mc-5", name: "Take 5 – Dispersão", startTime: 8.5, endTime: 11.5, easing: "linear",
      camera: { start: { position: { x: 0.3, y: 1.2, z: 4.5 }, lookAt: { x: 0, y: 1.1, z: 0 } },
                end:   { position: { x: 0.3, y: 1.2, z: 3.5 }, lookAt: { x: 0, y: 1.1, z: 0 } },
                fovStart: 52, fovEnd: 46 },
      lighting: lit(1.0, "#ffffff", 1, 0.45), fadeIn: false },
    { id: "mc-6", name: "Take 6 – Solo Estático", startTime: 11.5, endTime: 14.5, easing: "linear",
      camera: { start: { position: { x: 0.3, y: 1.2, z: 3.5 }, lookAt: { x: 0, y: 1.1, z: 0 } },
                end:   { position: { x: 0.3, y: 1.2, z: 3.5 }, lookAt: { x: 0, y: 1.1, z: 0 } },
                fovStart: 46, fovEnd: 46 },
      lighting: lit(1.0, "#ffffff", 1, 0.45), fadeIn: false },
    { id: "mc-7", name: "Take 7 – Logo Final", startTime: 14.5, endTime: 18.5, easing: "linear",
      camera: { start: { position: { x: 0.3, y: 1.2, z: 3.5 }, lookAt: { x: 0, y: 1.1, z: 0 } },
                end:   { position: { x: 0.3, y: 1.2, z: 3.5 }, lookAt: { x: 0, y: 1.1, z: 0 } },
                fovStart: 46, fovEnd: 46 },
      lighting: lit(1.0, "#ffffff", 1, 0.45), fadeIn: false },
  ],
}


// ─── Registro ─────────────────────────────────────────────────────────────────


// ─── TRIPLE DROP ─────────────────────────────────────────────────────────────
// 3 modelos caem do teto em sequência: laterais [0,2] primeiro, central [1] depois
// Requer TripleDrop engine (triple-drop-engine.js)

const TRIPLE_DROP: AnimationPreset = {
  name: "Triple Drop", version: 1, fadeColor: "#000000", fadeDuration: 0,
  // @ts-ignore
  useTripleDrop: true,
  takes: [
    // TAKE 1 (0→1s): 3 modelos convergem de triângulo para o centro empilhados em Z
    {
      id: "td-1", name: "Take 1 – Convergência", startTime: 0, endTime: 1, easing: "linear",
      camera: {
        start: { position: { x: 0, y: 1.2, z: 3.8 }, lookAt: { x: 0, y: 1.1, z: 0 } },
        end:   { position: { x: 0, y: 1.2, z: 3.8 }, lookAt: { x: 0, y: 1.1, z: 0 } },
        fovStart: 52, fovEnd: 52,
      },
      lighting: lit(1.0, "#ffffff", 1, 0.45),
      fadeIn: false,
    },
    // TAKE 2 (1→2s): câmera orbita para revelar profundidade — modelos parados
    {
      id: "td-2", name: "Take 2 – Reveal Depth", startTime: 1, endTime: 2, easing: "easeInOut",
      camera: {
        start: { position: { x: 0.0,  y: 1.2, z: 3.8  }, lookAt: { x: 0, y: 1.1, z: 0 } },
        end:   { position: { x: 2.18, y: 1.2, z: 3.11 }, lookAt: { x: 0, y: 1.1, z: 0 } },
        fovStart: 52, fovEnd: 52,
      },
      lighting: lit(1.0, "#ffffff", 1, 0.45),
      fadeIn: false,
    },
  ],
}

export const BUILTIN_PRESETS: BuiltinPreset[] = [
  {
    id: "cinematic-reveal", preset: CINEMATIC_REVEAL,
    label: "Cinematic Reveal",
    description: "O clássico. Silhueta, closes e reveal gradual com blinks dramáticos.",
    tags: ["clássico", "42s", "7 takes"],
    durationSec: 42, accentColor: "#6366f1",
    videoUrl: "/videos/presets/cinematic-reveal.mp4",
  },
  {
    id: "orbit-360", preset: ORBIT_360,
    label: "Orbit 360",
    description: "Giro completo 360° ao redor do modelo em velocidade constante.",
    tags: ["360°", "30s", "4 takes"],
    durationSec: 30, accentColor: "#0ea5e9",
    videoUrl: "/videos/presets/orbit-360.mp4",
  },
  {
    id: "editorial", preset: EDITORIAL,
    label: "Editorial",
    description: "Movimentos lentos e elegantes com iluminação quente. Estilo fashion.",
    tags: ["elegante", "46s", "5 takes"],
    durationSec: 46, accentColor: "#d97706",
    videoUrl: "/videos/presets/editorial.mp4",
  },
  {
    id: "quick-showcase", preset: QUICK_SHOWCASE,
    label: "Quick Showcase",
    description: "Dinâmico e direto. Frente, costas e detalhes em 30 segundos.",
    tags: ["dinâmico", "30s", "5 takes"],
    durationSec: 30, accentColor: "#8b5cf6",
    videoUrl: "/videos/presets/quick-showcase.mp4",
  },
  {
    id: "hype", preset: HYPE,
    label: "Hype",
    description: "Rápido e energético com flashes. Perfeito para streetwear.",
    tags: ["energético", "18s", "5 takes"],
    durationSec: 18, accentColor: "#ef4444",
    videoUrl: "/videos/presets/hype.mp4",
  },
  {
    id: "diagonal-flow", preset: DIAGONAL_FLOW,
    label: "Diagonal Flow",
    description: "Movimentos diagonais que criam profundidade e dinamismo naturais.",
    tags: ["dinâmico", "28s", "4 takes"],
    durationSec: 28, accentColor: "#14b8a6",
    videoUrl: "/videos/presets/diagonal-flow.mp4",
  },
  {
    id: "studio-clean", preset: STUDIO_CLEAN,
    label: "Studio Clean",
    description: "Quatro ângulos fixos com iluminação uniforme. Ideal para e-commerce.",
    tags: ["e-commerce", "32s", "4 takes"],
    durationSec: 32, accentColor: "#94a3b8",
    videoUrl: "/videos/presets/studio-clean.mp4",
  },
  {
    id: "atmosphere", preset: ATMOSPHERE,
    label: "Atmosphere",
    description: "Iluminação dramática azul/quente com transições lentas e atmosféricas.",
    tags: ["atmosférico", "36s", "4 takes"],
    durationSec: 36, accentColor: "#4060c0",
    videoUrl: "/videos/presets/atmosphere.mp4",
  },
  {
    id: "tres-angulos", preset: TRES_ANGULOS,
    label: "Três Ângulos",
    description: "Frente, lateral e costas em sequência. Simples e eficiente.",
    tags: ["completo", "30s", "4 takes"],
    durationSec: 30, accentColor: "#10b981",
    videoUrl: "/videos/presets/tres-angulos.mp4",
  },
  {
    id: "golden-hour", preset: GOLDEN_HOUR,
    label: "Golden Hour",
    description: "Iluminação quente estilo pôr do sol com contraluz e revelação dramática.",
    tags: ["premium", "38s", "5 takes"],
    durationSec: 18, accentColor: "#f59e0b",
    videoUrl: "/videos/presets/golden-hour.mp4",
  },
/*  {
    id: "cinematic-shirt", preset: CINEMATIC_SHIRT,
    label: "Cinematic Shirt",
    description: "8 shots cinematográficos: reveal, orbit 360°, closes de detalhe e retorno suave. FOV 35° fixo.",
    tags: ["cinema", "18s", "8 takes"],
    durationSec: 18, accentColor: "#a78bfa",
    videoUrl: "/videos/presets/cinematic-shirt.mp4",
  },*/
  {
    id: "multi-clone-reveal", preset: MULTI_CLONE_REVEAL,
    label: "Multi-Clone Reveal",
    description: "Explode em 5 clones em arco, gira, reduz para 1 e faz orbit. Igual ao vídeo de referência.",
    tags: ["clones", "10s", "5 takes", "especial"],
    durationSec: 18, accentColor: "#f59e0b",
    videoUrl: "/videos/presets/multi-clone-reveal.mp4",
  }
,
  /*{
    id: "triple-drop", preset: TRIPLE_DROP,
    label: "Triple Drop",
    description: "3 modelos caem do teto em sequência e se posicionam em trio.",
    tags: ["trio", "1s", "especial"],
    durationSec: 2, accentColor: "#38bdf8",
    videoUrl: "/videos/presets/triple-drop.mp4",
  }*/
]